import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import ClipCard from "@/components/ClipCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, TrendingUp, Zap, Swords, Flame } from "lucide-react";
import { Link } from "react-router-dom";

const ERAS = [
  { v: "all", label: "All Eras" },
  { v: "classic", label: "Classic (pre-'90)" },
  { v: "90s", label: "90s" },
  { v: "2000s", label: "2000s" },
  { v: "modern", label: "Modern" },
];

const STYLES = [
  { v: "all", label: "All Styles" },
  { v: "crowd_work", label: "Crowd Work" },
  { v: "dark", label: "Dark" },
  { v: "physical", label: "Physical" },
  { v: "musical", label: "Musical" },
  { v: "satire", label: "Satire" },
  { v: "storytelling", label: "Storytelling" },
  { v: "observational", label: "Observational" },
  { v: "absurd", label: "Absurd" },
];

export default function LeaderboardPage() {
  const [clips, setClips] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [era, setEra] = useState("all");
  const [style, setStyle] = useState("all");
  const [sort, setSort] = useState("upvotes");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/trending", { params: { days: 7, limit: 6 } })
      .then(({ data }) => setTrending(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    if (sort === "trending") {
      api.get("/trending", { params: { days: 7, category, limit: 60 } })
        .then(({ data }) => {
          let filtered = data;
          if (era !== "all") filtered = filtered.filter((c) => c.era === era);
          if (style !== "all") filtered = filtered.filter((c) => c.style === style);
          setClips(filtered);
        })
        .finally(() => setLoading(false));
    } else {
      api.get("/clips", { params: { category, era, style, sort } })
        .then(({ data }) => setClips(data))
        .finally(() => setLoading(false));
    }
  }, [category, era, style, sort]);

  const top = clips[0];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-12 pb-24 md:pb-12">
      {/* Hero */}
      <section className="relative mb-10 grid gap-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-7 relative overflow-hidden rounded-2xl border border-white/10 p-8 lg:p-10 grain"
          style={{
            background: "linear-gradient(135deg, rgba(255,184,0,0.12) 0%, rgba(11,12,16,0.4) 40%, rgba(255,46,85,0.1) 100%)",
          }}
        >
          <div className="stripes absolute inset-0 opacity-60" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-400/10 px-3 py-1 text-[10px] font-mono-ui uppercase tracking-widest text-amber-400">
              <Sparkles className="h-3 w-3" />
              The People's Comedy Court
            </div>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
              WHO'S THE
              <br />
              <span className="text-amber-400">FUNNIEST</span>{" "}
              <span className="italic text-[#FF2E55]">alive?</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-slate-300">
              Rank stand-up and sketch clips. Pit them head-to-head. Watch the ELO
              needle move as the world votes on who actually kills.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild data-testid="hero-battle-btn" className="bg-amber-400 text-black hover:bg-amber-300 font-bold btn-brutal">
                <Link to="/battle"><Swords className="mr-2 h-4 w-4" />Enter the Battle</Link>
              </Button>
              <Button asChild variant="outline" data-testid="hero-submit-btn" className="border-white/20 bg-transparent hover:bg-white/5 btn-brutal">
                <Link to="/submit"><Zap className="mr-2 h-4 w-4" />Submit a Clip</Link>
              </Button>
            </div>
            {stats && (
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                <Stat label="Clips" value={stats.total_clips} />
                <Stat label="Battles" value={stats.total_battles} />
                <Stat label="Votes" value={stats.total_votes} />
              </div>
            )}
          </div>
        </motion.div>

        {/* Top clip teaser */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="lg:col-span-5"
        >
          {top && (
            <div className="glass rounded-2xl p-5">
              <div className="mb-3 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#FF2E55]/40 bg-[#FF2E55]/10 px-3 py-1 text-[10px] font-mono-ui uppercase tracking-widest text-[#FF2E55]">
                  <TrendingUp className="h-3 w-3" />
                  #1 right now
                </div>
                <span className="font-mono-ui text-[10px] uppercase tracking-widest text-slate-400">
                  ELO {top.elo}
                </span>
              </div>
              <ClipCard clip={top} rank={1} />
            </div>
          )}
        </motion.div>
      </section>

      {/* Hot This Week strip */}
      {trending.length > 0 && (
        <section className="mb-10" data-testid="hot-week-section">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#FF2E55]/40 bg-[#FF2E55]/10 px-3 py-1 text-[10px] font-mono-ui uppercase tracking-widest text-[#FF2E55]">
                <Flame className="h-3 w-3" /> Hot this week
              </div>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl text-white tracking-wide">
                CLIPS ON A <span className="text-[#FF2E55]">HEATER</span>
              </h2>
              <p className="text-sm text-slate-400">Ranked by upvote velocity in the last 7 days.</p>
            </div>
            <button
              onClick={() => setSort("trending")}
              className="hidden sm:block text-xs font-mono-ui uppercase tracking-widest text-amber-400 hover:text-amber-300"
              data-testid="see-all-trending"
            >
              See all →
            </button>
          </div>
          <div className="scrollbar-thin flex gap-4 overflow-x-auto pb-3 snap-x">
            {trending.map((c, i) => (
              <Link
                to={`/clip/${c.id}`}
                key={c.id}
                data-testid={`hot-${c.id}`}
                className="snap-start shrink-0 w-72 card-neon rounded-xl p-3"
              >
                <div className="relative">
                  <YoutubeThumb youtubeId={c.youtube_id} title={c.title} />
                  <div className="absolute top-2 left-2 rounded-full bg-black/70 backdrop-blur px-2 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest text-[#FF2E55] border border-[#FF2E55]/40">
                    #{i + 1} · +{c.recent_upvotes || 0} this week
                  </div>
                </div>
                <p className="mt-3 font-display text-lg text-white leading-tight line-clamp-2">{c.title}</p>
                <p className="text-xs text-slate-400 mt-1"><span className="text-[#00F0FF]">◆</span> {c.comedian}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="mb-8">
        <Tabs value={category} onValueChange={setCategory} data-testid="category-tabs">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="all" data-testid="tab-all" className="data-[state=active]:bg-amber-400 data-[state=active]:text-black font-mono-ui uppercase text-xs tracking-widest">All</TabsTrigger>
              <TabsTrigger value="standup" data-testid="tab-standup" className="data-[state=active]:bg-amber-400 data-[state=active]:text-black font-mono-ui uppercase text-xs tracking-widest">Stand-up</TabsTrigger>
              <TabsTrigger value="skit" data-testid="tab-skit" className="data-[state=active]:bg-[#FF2E55] data-[state=active]:text-black font-mono-ui uppercase text-xs tracking-widest">Skits</TabsTrigger>
            </TabsList>

            <div className="flex flex-wrap gap-2">
              <FilterSelect testid="era-filter" value={era} onChange={setEra} items={ERAS} width="w-36" />
              <FilterSelect testid="style-filter" value={style} onChange={setStyle} items={STYLES} width="w-40" />
              <FilterSelect
                testid="sort-filter"
                value={sort}
                onChange={setSort}
                width="w-36"
                items={[
                  { v: "upvotes", label: "Most upvoted" },
                  { v: "elo", label: "Highest ELO" },
                  { v: "trending", label: "Trending (7d)" },
                  { v: "newest", label: "Newest" },
                ]}
              />
            </div>
          </div>
        </Tabs>
      </section>

      {/* Grid */}
      {loading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-96 animate-pulse rounded-xl bg-white/5" />
          ))}
        </div>
      ) : clips.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-16 text-center">
          <p className="font-display text-2xl text-slate-400">No clips match those filters</p>
          <p className="mt-2 text-sm text-slate-500">Try loosening the era or style.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {clips.map((clip, i) => (
            <ClipCard key={clip.id} clip={clip} rank={i + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3">
      <div className="font-display text-3xl text-white">{value}</div>
      <div className="mt-0.5 font-mono-ui text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
    </div>
  );
}

function YoutubeThumb({ youtubeId, title }) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-white/10 bg-black">
      <img
        src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
        alt={title}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition group-hover:bg-black/10">
        <div className="rounded-full bg-black/70 p-2.5">
          <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, items, testid, width = "w-36" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`${width} border-white/10 bg-white/5 text-xs font-mono-ui uppercase tracking-widest text-white`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="border-white/10 bg-[#12141C] text-white">
        {items.map((it) => (
          <SelectItem key={it.v} value={it.v} className="text-xs font-mono-ui uppercase tracking-widest">
            {it.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
